import React from "react";
import { Table, Tag, Button } from "antd";
import { LinkedinOutlined } from "@ant-design/icons";
import { Company } from "../types";
import styles from "../styles/companies.module.css";

interface CompanyTableProps {
  companies: Company[];
  loading: boolean;
}

const CompanyTable: React.FC<CompanyTableProps> = ({ companies, loading }) => {
  const columns = [
    {
      title: "",
      dataIndex: "logo",
      key: "logo",
      render: (text: string) =>
        text ? (
          <img
            src={text || "https://via.placeholder.com/150?text=No+Logo"}
            alt="Company Logo"
            className={styles.tableLogo}
            onError={(e) => {
              e.currentTarget.onerror = null; // Prevent infinite loop
              e.currentTarget.src =
                "https://via.placeholder.com/150?text=No+Logo";
            }}
          />
        ) : (
          <div className={styles.noLogo}>N/A</div>
        ),
      width: 80, // Example width for the logo column
    },
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: Company, b: Company) => a.name.localeCompare(b.name),
      render: (text: string) => <span className={styles.boldText}>{text}</span>,
      width: 150, // Example width
    },
    {
      title: "Careers",
      dataIndex: "careers",
      key: "careers",
      render: (text: string) =>
        text ? (
          <Button
            type="primary"
            href={text}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: "#f42722",
              color: "white",
              border: "none",
            }}
          >
            Hire Me
          </Button>
        ) : null,
      width: 100, // Example width
    },
    {
      /* 
         DESCRIPTION COLUMN: 
         - We set a fixed width (e.g., 300px).
         - We rely on CSS (white-space, word-break) for wrapping text instead of truncating it.
      */
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string) => <span>{text}</span>,
      width: 300, // Limit the column width
    },
    {
      title: "Funding Stage",
      dataIndex: "fundingStage",
      key: "fundingStage",
      sorter: (a: Company, b: Company) =>
        a.fundingStage.localeCompare(b.fundingStage),
      width: 150,
    },
    // Location column after Funding Stage
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 150,
    },
    {
      title: "Employees",
      dataIndex: "employees",
      key: "employees",
      sorter: (a: Company, b: Company) =>
        parseInt(a.employees) - parseInt(b.employees),
      width: 120,
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
      render: (text: string) => (
        <a href={text} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ),
      width: 180,
    },
    {
      title: "Linkedin",
      dataIndex: "linkedin",
      key: "linkedin",
      render: (text: string) => (
        <a href={text} target="_blank" rel="noopener noreferrer">
          <LinkedinOutlined style={{ fontSize: "20px" }} />
        </a>
      ),
      width: 100,
    },
    {
      title: "Primary Sector",
      dataIndex: "primarySector",
      key: "primarySector",
      sorter: (a: Company, b: Company) =>
        a.primarySector.localeCompare(b.primarySector),
      render: (text: string) =>
        text?.split(",").map((tag: string) => <Tag key={tag}>{tag}</Tag>),
      width: 200,
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={companies}
      loading={loading}
      pagination={{ pageSize: 8, position: ["bottomLeft"] }}
      rowKey="name"
      /*
         SCROLL PROPERTY:
         - Allows horizontal scrolling when total column widths exceed the table container’s width.
         - 'max-content' automatically adjusts to column widths, or you can set a numeric value (e.g., 1200).
      */
      scroll={{ x: "max-content" }}
    />
  );
};

export default CompanyTable;
